import { useState } from 'react';
import type { ShowcaseItem } from '../utils/ShowcaseProvider';
import { Utilities } from '../utils/Utilities';

interface ShowcaseCardProps {
    item: ShowcaseItem;
}

/**
 * Overview card for one showcase: preview image and title in the header,
 * description, author and date in the body. Links to the repository folder.
 */
function ShowcaseCard({ item }: ShowcaseCardProps) {
    const [imageFailed, setImageFailed] = useState(false);
    const hasImage = !!item.imageUrl && !imageFailed;
    const hasLink = !!item.folderUrl;

    const content = (
        <>
            <div className={`showcase-card__header${hasImage ? '' : ' showcase-card__header--no-image'}`}>
                {hasImage && (
                    <img
                        src={item.imageUrl}
                        alt=""
                        loading="lazy"
                        className="showcase-card__image"
                        onError={() => setImageFailed(true)}
                    />
                )}
                <h3 className="showcase-card__title">{item.title}</h3>
            </div>
            <div className="showcase-card__body">
                {item.description && <p className="showcase-card__description">{item.description}</p>}
                {item.tags.length > 0 && (
                    <ul className="showcase-card__tags" aria-label="Tags">
                        {item.tags.map((tag) => (
                            <li key={tag}><span className="badge showcase-card__tag">{tag}</span></li>
                        ))}
                    </ul>
                )}
                <div className="showcase-card__meta">
                    {item.author && (
                        <span><i className="bi bi-person"></i> {item.author}</span>
                    )}
                    {item.date && (
                        <span><i className="bi bi-calendar"></i> {Utilities.formatDate(item.date.toISOString())}</span>
                    )}
                </div>
                {hasLink && (
                    <span className="showcase-card__link">
                        View in repository <i className="bi bi-box-arrow-up-right"></i>
                    </span>
                )}
            </div>
        </>
    );

    return (
        <article className="showcase-card">
            {hasLink ? (
                <a href={item.folderUrl} target="_blank" rel="noopener noreferrer" className="showcase-card__anchor">
                    {content}
                </a>
            ) : (
                content
            )}
        </article>
    );
}

export default ShowcaseCard;
